import { Switch } from '@thanx/uikit/switch'
import Info from 'components/Info'
import ModifierList from 'components/ModifierList'
import ProductList from 'components/ProductList'
import useCurrentMerchant from 'hooks/useCurrentMerchant'
import { buildTranslate } from 'locales'
import isEmpty from 'lodash/isEmpty'
import { Fields as RedeemTemplate } from 'models/RedeemTemplate'
import {
  getDiscountedModifiersText,
  getDiscountedProductsText,
} from 'scenes/RedeemManager/helpers'
import Card from '../Card'
import HiddenMenu from './HiddenMenu'

type OnlineRedemptionCardProps = {
  template: RedeemTemplate
}

const t = buildTranslate('redeem_manager.detail.cards.online_redemption')

const OnlineRedemptionCard = ({ template }: OnlineRedemptionCardProps) => {
  const { product_ids, redemption_venue, type } = template
  const isOnlineVenue =
    redemption_venue === 'online' || redemption_venue === 'all'
  const digitalProductIds = product_ids?.digital || {}
  const requiredProductIds = product_ids?.digital?.required || []
  const isModifierSubtype = template.subtype === 'modifier'
  // For modifier subtypes: discounted = modifier IDs, discounted_related = product IDs
  // For item subtypes: discounted = product IDs, discounted_related = modifier IDs
  const discountedProductIds =
    (isModifierSubtype
      ? digitalProductIds?.discounted_related
      : digitalProductIds?.discounted) || []
  const discountedModifierIds =
    (isModifierSubtype
      ? digitalProductIds?.discounted
      : digitalProductIds?.discounted_related) || []
  const modifierDiscountIds = digitalProductIds?.discounted_modifiers || []

  const merchant = useCurrentMerchant()
  const { ordering_enabled } = merchant || {}
  const isAvailable = isOnlineVenue && !!ordering_enabled
  const isFreeItem = template.type === 'manual/item'
  const displayRequiredProducts = !isEmpty(requiredProductIds)
  const displayDiscountedProducts =
    !isEmpty(discountedProductIds) && !!discountedProductIds?.[0]
  const displayDiscountedModifierIds =
    !isEmpty(discountedModifierIds) && !!discountedModifierIds?.[0]
  const displayModifierDiscountIds = !isEmpty(modifierDiscountIds)
  const isHiddenMenu = type === 'experience/hidden_menu'
  const ableToRedeem =
    !displayRequiredProducts && !displayDiscountedProducts && !isHiddenMenu

  const getDiscountedLabel = (type: 'modifier' | 'item') => {
    const isModifierLabel = Boolean(type === 'modifier')

    if (isModifierLabel) {
      return getDiscountedModifiersText(template.subtype).label
    }

    if (isModifierSubtype && isFreeItem) {
      return t('free_modifier_items')
    }

    if (isModifierSubtype) {
      return t('discounted_modifier_items')
    }

    return getDiscountedProductsText(isFreeItem, template.subtype).label
  }

  return (
    <Card title={t('title')} info={t('info')} isActive={isAvailable}>
      <Switch
        condition={isAvailable}
        fallback={
          <p className="grey-70">
            <Switch
              condition={!ordering_enabled}
              fallback={t('not_redeemable')}
            >
              {t('not_available')}
              <a href="mailto:merchant.support@thanx.com">{t('contact_us')}</a>
            </Switch>
          </p>
        }
      >
        <Switch condition={isHiddenMenu}>
          <HiddenMenu
            categoryName={template.category_name}
            imageSrc={template?.images?.advertising_image?.small}
          />
        </Switch>
        <Switch condition={ableToRedeem}>
          <p className="mb-s grey-70">{t('able_to_redeem')}</p>
        </Switch>
        {displayRequiredProducts && (
          <Info
            className="mb-m"
            label={t('items_required')}
            value={<ProductList productIds={requiredProductIds} />}
          />
        )}
        <Switch
          condition={displayRequiredProducts && !displayDiscountedProducts}
        >
          <p className="body-text-14 bold">{t('then_entire_purchase')}</p>
        </Switch>
        {displayDiscountedProducts && (
          <>
            <Info
              label={getDiscountedLabel('item')}
              value={<ProductList productIds={discountedProductIds} />}
            />

            <Info
              className={`mb-m ${!displayDiscountedModifierIds && 'mt-m'}`}
              label={getDiscountedLabel('modifier')}
              value={
                <Switch
                  condition={displayDiscountedModifierIds}
                  fallback={<p className="grey-70">{t('no_modifiers')}</p>}
                >
                  <ModifierList
                    modifierIds={discountedModifierIds}
                    productIds={discountedProductIds}
                  />
                </Switch>
              }
            />

            <Switch condition={displayModifierDiscountIds}>
              <Info
                className="mb-m"
                label={t('modifier_discounts')}
                value={
                  <ModifierList
                    modifierIds={modifierDiscountIds}
                    productIds={discountedProductIds}
                  />
                }
              />
            </Switch>

            <p className="grey-70">
              <Switch condition={isModifierSubtype}>
                {isFreeItem
                  ? t('will_apply_lowest_free_modifier')
                  : t('will_apply_lowest')}
              </Switch>
              <Switch condition={!isModifierSubtype}>
                {isFreeItem
                  ? t('will_apply_lowest_free_item')
                  : t('will_apply_lowest')}
              </Switch>
            </p>
          </>
        )}
      </Switch>
    </Card>
  )
}

export default OnlineRedemptionCard
